/*
 * Reading an uploaded freight cost xlsx file.
 * It says what kind of file it is willing to read at all,
 * and turns the worksheets into rows.
 * Interpreting the cells and checking them is not happening here (it is the job of the validation).
 */

import * as XLSX from 'xlsx';

import { XlsxRow, XlsxSheet } from '../../../shared/types/xlsx';
import { isEmptyRow } from '../../../shared/utils/xlsx';

/**
 * Upper limit of an uploaded file.
 * Checked on the field, before anything reads the file.
 */
export const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

/**
 * The formats the reading can handle.
 */
export const ACCEPTED_FILE_EXTENSIONS = ['.xlsx', '.xls', '.ods'];

/**
 * Drops the rows the used range of a sheet reaches over.
 * Xlsx keeps the range it once needed, so it often ends below the data
 * (after deleting rows, or where only the formatting was touched).
 *
 * @param {XlsxRow[]} rows the rows of the used range
 * @returns {XlsxRow[]} the rows up to the last one holding anything
 */
function dropTrailingEmptyRows(rows: XlsxRow[]): XlsxRow[] {
  let rowCount = rows.length;

  while (rowCount > 0 && isEmptyRow(rows[rowCount - 1])) {
    rowCount--;
  }

  return rows.slice(0, rowCount);
}

/**
 * The range a worksheet holds, from A1 to its furthest cell.
 *
 * The range starts at A1, so every cell keeps the reference it has in the file, and a problem
 * can be pointed at by that reference.
 *
 * Note:
 * The range the file declares ("!ref") is not used, because it can be short:
 * an .ods file stores a value repeating in neighbouring cells as one cell with a repeat count
 * (ODF 19.675.2), and the xlsx parser writes out every repeat as a cell, but counts only the
 * first one into the range it reports.
 * Reading by that range would silently drop a whole column of freight costs.
 *
 * @param {XLSX.WorkSheet} sheet the worksheet to look at
 * @returns {string | undefined} the range to read, or undefined when the sheet holds no cell
 */
function getSheetRange(sheet: XLSX.WorkSheet): string | undefined {
  let lastRow = -1;
  let lastColumn = -1;

  for (const cellAddress of Object.keys(sheet)) {
    // the sheet holds its own settings as well, all of them prefixed
    if (cellAddress.startsWith('!')) {
      continue;
    }

    const { r: row, c: column } = XLSX.utils.decode_cell(cellAddress);
    lastRow = Math.max(lastRow, row);
    lastColumn = Math.max(lastColumn, column);
  }

  return lastRow < 0
    ? undefined
    : // eslint-disable-next-line id-length
      XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: lastRow, c: lastColumn } });
}

/**
 * Rows of one worksheet, keeping every cell where it is in file.
 *
 * @param {XLSX.WorkSheet} sheet the worksheet to read
 * @returns {XlsxRow[]} the rows of the worksheet
 */
function readSheetRows(sheet: XLSX.WorkSheet): XlsxRow[] {
  const range = getSheetRange(sheet);
  if (!range) {
    return [];
  }

  const rows = XLSX.utils.sheet_to_json<XlsxRow>(sheet, {
    header: 1,
    // the numbers are needed as they are, not as xlsx shows them
    raw: true,
    defval: null,
    blankrows: true,
    range
  });

  return dropTrailingEmptyRows(rows);
}

/**
 * Reads an uploaded file into the sheets of the factories.
 *
 * @param {File} file the uploaded file (xlsx, xls or ods)
 * @returns {Promise<XlsxSheet[]>} one sheet per worksheet, named after it
 */
export default async function readFreightCostSheets(file: File): Promise<XlsxSheet[]> {
  const workbook = XLSX.read(await file.arrayBuffer());

  return workbook.SheetNames.map((name) => ({
    name,
    rows: readSheetRows(workbook.Sheets[name])
  }));
}
